import { motion, useReducedMotion } from "framer-motion";

import styles from "./experience.module.css";
import { experienceRoles, ExperienceRole, formatMonth, formatTenure } from "../../../constants/experience";

/**
 * ============================================================================
 * Work Experience — the alternating glass timeline.
 * ============================================================================
 *
 * SECTIONWRAPPER IS GONE (D-061), and dropping it is what let this section
 * join the shared content rail. That HOC wrapped the component in a SECOND
 * `<motion.section>` carrying `max-w-7xl mx-auto` plus the starter template's
 * `sm:px-16 px-6` padding — a rail of its own, nested inside the one this
 * component declares, and the reason Experience measured a 1148px content box
 * at 1280 where four other sections measured 1020. Its only other
 * contribution was a `staggerContainer` variant, which every RoleCard already
 * duplicates with its own `whileInView`; nothing was orchestrating anything.
 *
 * It had exactly one consumer — this file — so the HOC, `hoc/index.ts`, the
 * template style map it imported and `utils/motion.ts` all became orphans in
 * the same commit and are deleted with it (Article V.6).
 */

/** First letters of up to two words — the node marker when no logo is supplied. */
const initialsOf = (company: string): string =>
	company
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0])
		.join("")
		.toUpperCase();

const RoleCard = ({ role, index }: { role: ExperienceRole; index: number }) => {
	const reduceMotion = useReducedMotion();
	const isCurrent = role.end === null;
	const tenure = formatTenure(role.start, role.end);
	/* Odd entries sit right of the spine, even ones left. */
	const flipped = index % 2 === 1;

	const dateRange = `${formatMonth(role.start)} — ${role.end ? formatMonth(role.end) : "Present"}`;

	return (
		<motion.li
			className={`${styles.item} ${flipped ? styles.flipped : ""}`}
			initial={reduceMotion ? false : { opacity: 0, y: 28 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.2 }}
			transition={{ duration: 0.55, delay: Math.min(index * 0.06, 0.24), ease: [0.22, 1, 0.36, 1] }}
		>
			{/* ---------- the card ----------
			    `card-shell` / `card-face` is the site-wide treatment (D-061).
			    What it replaces was a fourth card material: a `--panel-fill`
			    white veil under an 18px backdrop-blur, an 18px radius, a 1px
			    LIT EDGE that flipped to whichever side faced the spine, and a
			    specular wash from the same direction.

			    THE LIT EDGE AND THE SPECULAR WASH ARE DELIBERATELY LOST, and
			    they are the one piece of craft this pass gives up. They were a
			    genuine idea — the spine reads as the light source — but the
			    gradient hairline IS a directional light, fixed at
			    top-left-to-bottom-right, and two directional light sources on
			    one card, one of which mirrors per row, is not a subtler
			    version of the effect. The timeline's identity is carried by
			    the spine, the pulsing current node and the alternation, none
			    of which move. */}
			<div className={`card-shell ${styles.panel}`}>
				<article className={`card-face ${styles.panelFace}`}>
					<header className={styles.head}>
						<div className={styles.headMain}>
							<h3 className={styles.role}>{role.role}</h3>
							{/* NO "formerly …" LINE. The second place the earlier
							    title reached a reader, removed with the pill on
							    2026-09-06 — leaving one of the two would have made the
							    same page say both things at once. */}
							<p className={styles.company}>
								{role.companyUrl ? (
									<a href={role.companyUrl} target="_blank" rel="noopener noreferrer">
										{role.company}
									</a>
								) : (
									role.company
								)}
								{role.employmentType && (
									<>
										<span className={styles.dot}>·</span>
										<span className={styles.nowrap}>{role.employmentType}</span>
									</>
								)}
							</p>
						</div>

						{/* The tenure chip joins the shared tinted-label vocabulary
						    (D-061). It was a blue chip — `--spine-rgb` fill,
						    `--steel-rgb` edge, `--steel-text-rgb` ink — which is
						    the site's third indigo-ish pill for one idea. */}
						{/* ------------------------------------------------ the meta rail --
						    Two pills now, so they need a box of their own: `.head` is
						    `justify-content: space-between` and a second direct child
						    would have been distributed rather than grouped. It wraps
						    rather than stacking, so a narrow card drops the tenure
						    under the state instead of squeezing the title. */}
						<div className={styles.headMeta}>
							{/* THE CURRENT ROLE SAYS SO, IN WORDS (D-100, owner
							    instruction). The timeline has always KNOWN which role
							    is current — `isCurrent` drives the pulsing spine node
							    and the aside already reads "Present" — but on the card
							    itself the state was carried only by a date range a
							    reader has to parse and by a colour, and neither is a
							    label. Emerald because emerald already means "current"
							    on this site: it is the colour `figures.ts` gives the
							    open-ended bar on the dashboard, so the two surfaces
							    agree rather than each choosing a hue.

							    The dot is decoration and the word is the information,
							    which is the rule that keeps this out of WCAG 1.4.1 —
							    nothing here is distinguishable by colour alone. */}
							{isCurrent && (
								<span className="pill-tint shrink-0 border-[var(--cat-emerald-border)] bg-[var(--cat-emerald-fill)] text-[var(--cat-emerald-text)]">
									<span aria-hidden="true" className={styles.currentDot} />
									Current
								</span>
							)}
							{tenure && (
								<span className="pill-tint shrink-0 tabular-nums border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] text-[var(--cat-indigo-text)]">
									{tenure}
								</span>
							)}
						</div>
					</header>

					{/* Repeated inside the card for narrow screens, where the aside is hidden. */}
					<p className={styles.metaInline}>
						<span className={styles.dates}>{dateRange}</span>
						{role.location && (
							<>
								<span className={styles.dot}>·</span>
								{role.location}
							</>
						)}
					</p>

					{role.placeholder && (
						<p className={styles.placeholder}>
							Sample entry — replace in <code>src/constants/experience.ts</code>
						</p>
					)}

					{role.summary && <p className={styles.summary}>{role.summary}</p>}

					{role.highlights.length > 0 && (
						<ul className={styles.highlights}>
							{role.highlights.map((point: string) => (
								<li key={point}>{point}</li>
							))}
						</ul>
					)}

					{role.stack.length > 0 && (
						<ul className={styles.stack} aria-label="Technologies used">
							{role.stack.map((tech: string) => (
								<li key={tech}>{tech}</li>
							))}
						</ul>
					)}
				</article>
			</div>

			{/* ---------- spine node ---------- */}
			<div className={styles.rail}>
				<span className={`${styles.node} ${isCurrent ? styles.nodeCurrent : ""}`}>
					{role.logo ? (
						/* The same plate Education's crests sit on (index.css, D-097) —
						   one definition, because a disc that must look identical inside
						   two spine nodes is exactly what D-061 said not to copy. */
						<span className="logo-plate">
							<img src={role.logo} alt="" aria-hidden="true" loading="lazy" decoding="async" />
						</span>
					) : (
						<span className={styles.initials} aria-hidden="true">
							{initialsOf(role.company)}
						</span>
					)}
				</span>
			</div>

			{/* ---------- date, opposite the card ---------- */}
			<aside className={styles.aside} aria-hidden="true">
				<span className={styles.asideDate}>{dateRange}</span>
				{role.location && <span className={styles.asideLocation}>{role.location}</span>}
			</aside>
		</motion.li>
	);
};

const Experience_v2 = () => (
	<section className="section-shell" id="experience" aria-labelledby="experience-heading">
		<div className={`section-rail ${styles.section}`}>
			<header className={styles.sectionHead}>
				{/* Eyebrow and heading on the site-wide rules (D-061). The heading
				    was 44px/700 against the site's 48px/700 — near enough to look
				    like a mistake rather than a decision, which is the worst kind
				    of inconsistency. The words are unchanged. */}
				<p className="section-eyebrow">What I have done so far</p>
				<h2 id="experience-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Work
					</span>{" "}
					Experience
				</h2>
			</header>

			<ol className={styles.timeline}>
				{experienceRoles.map((role: ExperienceRole, index: number) => (
					<RoleCard key={role.id} role={role} index={index} />
				))}
			</ol>
		</div>
	</section>
);

/* The id lives on the <section> above, and only there. */
export default Experience_v2;
